import { cn } from '../../lib/utils'
import {
  AccountType, ConnectionStatus, GoalStatus,
  ACCOUNT_TYPE_LABEL, GOAL_STATUS_LABEL, CONNECTION_STATUS_LABEL,
} from '../../lib/enums'

type BadgeVariant = 'default' | 'primary' | 'income' | 'expense' | 'warning' | 'info' | 'muted'

const VARIANTS: Record<BadgeVariant, string> = {
  default: 'bg-surface3 text-white',
  primary: 'bg-primary/10 text-primary',
  income: 'bg-income/10 text-income',
  expense: 'bg-expense/10 text-expense',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
  muted: 'bg-surface3 text-muted',
}

export function Badge({ children, className, variant = 'default' }: { children: React.ReactNode; className?: string; variant?: BadgeVariant }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-2 py-0.5 text-2xs font-semibold uppercase whitespace-nowrap', VARIANTS[variant], className)}>
      {children}
    </span>
  )
}

/** Só aparece quando a leitura está somando mais de uma conta. */
export function DonoBadge({ nome }: { nome?: string }) {
  if (!nome) return null
  return (
    <Badge variant="primary" className="normal-case">
      <i className="ri-user-shared-line" aria-hidden /> {nome.split(' ')[0]}
    </Badge>
  )
}

export function StatusDot({ status }: { status: ConnectionStatus }) {
  const colors: Record<ConnectionStatus, string> = {
    [ConnectionStatus.Active]: 'bg-income',
    [ConnectionStatus.Expired]: 'bg-muted',
    [ConnectionStatus.Error]: 'bg-expense',
    [ConnectionStatus.Pending]: 'bg-warning',
  }
  return <span className={cn('inline-block h-2 w-2 flex-shrink-0 rounded-full', colors[status] ?? 'bg-muted')} aria-hidden />
}

export function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  const variants: Record<ConnectionStatus, BadgeVariant> = {
    [ConnectionStatus.Active]: 'income',
    [ConnectionStatus.Expired]: 'muted',
    [ConnectionStatus.Error]: 'expense',
    [ConnectionStatus.Pending]: 'warning',
  }
  return <Badge variant={variants[status] ?? 'muted'}>{CONNECTION_STATUS_LABEL[status] ?? String(status)}</Badge>
}

export function AccountTypeBadge({ type }: { type: AccountType }) {
  const variants: Record<AccountType, BadgeVariant> = {
    [AccountType.Checking]: 'info',
    [AccountType.Savings]: 'income',
    [AccountType.Investment]: 'warning',
    [AccountType.CreditCard]: 'expense',
    [AccountType.Cash]: 'muted',
    [AccountType.Benefit]: 'primary',
  }
  return <Badge variant={variants[type] ?? 'muted'}>{ACCOUNT_TYPE_LABEL[type] ?? String(type)}</Badge>
}

export function GoalStatusBadge({ status }: { status: GoalStatus }) {
  const variants: Record<GoalStatus, BadgeVariant> = {
    [GoalStatus.Active]: 'info',
    [GoalStatus.Completed]: 'income',
    [GoalStatus.Cancelled]: 'expense',
  }
  return <Badge variant={variants[status] ?? 'muted'}>{GOAL_STATUS_LABEL[status] ?? String(status)}</Badge>
}
